/**
 * ScheduleList Component
 * Displays a grid of workflow and system task schedules with actions
 */

import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Plus, Calendar, Search, RefreshCw, Workflow, Settings } from 'lucide-react';
import { ScheduleCard } from './ScheduleCard';
import {
  useScheduleList,
  useToggleSchedule,
  useRunScheduleNow,
  useDeleteSchedule,
} from '../hooks/useWorkflowSchedules';
import {
  useSystemTaskScheduleList,
  useToggleSystemTaskSchedule,
  useRunSystemTaskScheduleNow,
  useDeleteSystemTaskSchedule,
} from '../hooks/useSystemTaskSchedules';
import type { WorkflowSchedule, ScheduleType, ScheduleStatus } from '@/types/workflowSchedule';
import type { SystemTaskSchedule, ScheduleCategory } from '@/types/systemTaskSchedule';
import { SCHEDULE_TYPE_LABELS, SCHEDULE_STATUS_LABELS } from '@/types/workflowSchedule';
import { SCHEDULE_CATEGORY_LABELS } from '@/types/systemTaskSchedule';

type Schedule = WorkflowSchedule | SystemTaskSchedule;
type CategoryFilter = ScheduleCategory | 'all';

interface ScheduleListProps {
  onEdit?: (schedule: Schedule, category: ScheduleCategory) => void;
  onCreate?: (category?: ScheduleCategory) => void;
  onViewHistory?: (scheduleId: string, scheduleName?: string, category?: ScheduleCategory) => void;
}

export function ScheduleList({
  onEdit,
  onCreate,
  onViewHistory,
}: ScheduleListProps) {
  // Category filter
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<ScheduleStatus | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<ScheduleType | 'all'>('all');

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<{ schedule: Schedule; category: ScheduleCategory } | null>(null);

  // Build query params for workflow schedules
  const workflowParams = {
    ...(statusFilter !== 'all' && { status: statusFilter }),
    ...(typeFilter !== 'all' && { schedule_type: typeFilter }),
  };

  // Build query params for system task schedules
  const systemTaskParams = {
    ...(statusFilter !== 'all' && categoryFilter === 'system_task' && {}),
  };

  // Data fetching - Workflow schedules
  const {
    data: workflowData,
    isLoading: workflowLoading,
    error: workflowError,
    refetch: refetchWorkflow,
    isFetching: workflowFetching,
  } = useScheduleList(workflowParams);

  // Data fetching - System task schedules
  const {
    data: systemTaskData,
    isLoading: systemTaskLoading,
    error: systemTaskError,
    refetch: refetchSystemTask,
    isFetching: systemTaskFetching,
  } = useSystemTaskScheduleList(systemTaskParams);

  // Mutations - Workflow
  const toggleWorkflowSchedule = useToggleSchedule();
  const runWorkflowNow = useRunScheduleNow();
  const deleteWorkflowSchedule = useDeleteSchedule();

  // Mutations - System Task
  const toggleSystemTaskSchedule = useToggleSystemTaskSchedule();
  const runSystemTaskNow = useRunSystemTaskScheduleNow();
  const deleteSystemTaskSchedule = useDeleteSystemTaskSchedule();

  // Combined loading/error states
  const isLoading = workflowLoading || systemTaskLoading;
  const isFetching = workflowFetching || systemTaskFetching;
  const error = workflowError || systemTaskError;

  // Handlers
  const handleToggle = async (id: string, enabled: boolean, category: ScheduleCategory) => {
    try {
      if (category === 'workflow') {
        await toggleWorkflowSchedule.mutateAsync({ id, enabled });
      } else {
        await toggleSystemTaskSchedule.mutateAsync({ id, enabled });
      }
    } catch {
      // Error handled by hook
    }
  };

  const handleRunNow = async (id: string, category: ScheduleCategory) => {
    try {
      if (category === 'workflow') {
        await runWorkflowNow.mutateAsync(id);
      } else {
        await runSystemTaskNow.mutateAsync(id);
      }
    } catch {
      // Error handled by hook
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.category === 'workflow') {
        await deleteWorkflowSchedule.mutateAsync(deleteTarget.schedule.id);
      } else {
        await deleteSystemTaskSchedule.mutateAsync(deleteTarget.schedule.id);
      }
      setDeleteTarget(null);
    } catch {
      // Error handled by hook
    }
  };

  const handleRefresh = () => {
    if (categoryFilter === 'all' || categoryFilter === 'workflow') {
      refetchWorkflow();
    }
    if (categoryFilter === 'all' || categoryFilter === 'system_task') {
      refetchSystemTask();
    }
  };

  // Combine and filter schedules
  const workflowSchedules = workflowData?.schedules || [];
  const systemTaskSchedules = systemTaskData?.schedules || [];

  // Apply category filter and search
  const getFilteredSchedules = (): Array<{ schedule: Schedule; category: ScheduleCategory }> => {
    const items: Array<{ schedule: Schedule; category: ScheduleCategory }> = [];

    if (categoryFilter === 'all' || categoryFilter === 'workflow') {
      workflowSchedules.forEach((s) => items.push({ schedule: s, category: 'workflow' }));
    }
    if (categoryFilter === 'all' || categoryFilter === 'system_task') {
      systemTaskSchedules.forEach((s) => items.push({ schedule: s, category: 'system_task' }));
    }

    // Apply search filter
    if (!searchQuery) return items;
    const query = searchQuery.toLowerCase();
    return items.filter(({ schedule, category }) => {
      const nameMatch = schedule.name.toLowerCase().includes(query);
      const descMatch = schedule.description?.toLowerCase().includes(query);
      if (category === 'workflow') {
        const wf = schedule as WorkflowSchedule;
        const workflowMatch = wf.workflow_name?.toLowerCase().includes(query);
        return nameMatch || descMatch || workflowMatch;
      }
      return nameMatch || descMatch;
    });
  };

  const filteredSchedules = getFilteredSchedules();

  // Counts
  const totalWorkflow = workflowData?.total || 0;
  const totalSystemTask = systemTaskData?.total || 0;
  const totalCount = categoryFilter === 'all'
    ? totalWorkflow + totalSystemTask
    : categoryFilter === 'workflow'
      ? totalWorkflow
      : totalSystemTask;

  // Status filter options
  const statusOptions = [
    { value: 'all', label: '全部状态' },
    ...Object.entries(SCHEDULE_STATUS_LABELS).map(([value, label]) => ({
      value,
      label,
    })),
  ];

  // Type filter options
  const typeOptions = [
    { value: 'all', label: '全部类型' },
    ...Object.entries(SCHEDULE_TYPE_LABELS).map(([value, label]) => ({
      value,
      label,
    })),
  ];

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2" />
          <p className="text-gray-500">加载中...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center text-red-500">
          <p>加载失败</p>
          <p className="text-sm">{(error as Error).message}</p>
          <Button variant="secondary" className="mt-4" onClick={handleRefresh}>
            重试
          </Button>
        </div>
      </div>
    );
  }

  const isActionLoading =
    toggleWorkflowSchedule.isPending ||
    runWorkflowNow.isPending ||
    deleteWorkflowSchedule.isPending ||
    toggleSystemTaskSchedule.isPending ||
    runSystemTaskNow.isPending ||
    deleteSystemTaskSchedule.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            定时任务
          </h2>
          <p className="text-sm text-gray-500">
            {totalCount} 个定时任务
            {categoryFilter === 'all' && ` (工作流: ${totalWorkflow}, 系统: ${totalSystemTask})`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={handleRefresh}
            disabled={isFetching}
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
            刷新
          </Button>
          {onCreate && (
            <Button variant="primary" onClick={() => onCreate()}>
              <Plus className="h-4 w-4 mr-1" />
              创建任务
            </Button>
          )}
        </div>
      </div>

      {/* Category Tabs */}
      <Tabs value={categoryFilter} onValueChange={(v) => setCategoryFilter(v as CategoryFilter)}>
        <TabsList>
          <TabsTrigger value="all" className="flex items-center gap-2">
            全部
            <span className="text-xs bg-gray-200 px-1.5 py-0.5 rounded">
              {totalWorkflow + totalSystemTask}
            </span>
          </TabsTrigger>
          <TabsTrigger value="workflow" className="flex items-center gap-2">
            <Workflow className="h-4 w-4" />
            {SCHEDULE_CATEGORY_LABELS.workflow}
            <span className="text-xs bg-gray-200 px-1.5 py-0.5 rounded">{totalWorkflow}</span>
          </TabsTrigger>
          <TabsTrigger value="system_task" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            {SCHEDULE_CATEGORY_LABELS.system_task}
            <span className="text-xs bg-gray-200 px-1.5 py-0.5 rounded">{totalSystemTask}</span>
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="搜索任务名称..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex gap-2">
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as ScheduleStatus | 'all')}
            options={statusOptions}
          />
          <Select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as ScheduleType | 'all')}
            options={typeOptions}
          />
        </div>
      </div>

      {/* Empty state */}
      {filteredSchedules.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Calendar className="h-12 w-12 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium mb-1">
              {searchQuery || statusFilter !== 'all' || typeFilter !== 'all'
                ? '没有匹配的任务'
                : categoryFilter === 'system_task'
                  ? '暂无系统定时任务'
                  : '暂无定时任务'}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {searchQuery || statusFilter !== 'all' || typeFilter !== 'all'
                ? '尝试调整筛选条件'
                : '创建您的第一个定时任务开始使用'}
            </p>
            {onCreate && !searchQuery && statusFilter === 'all' && typeFilter === 'all' && (
              <Button
                variant="secondary"
                onClick={() => onCreate(categoryFilter !== 'all' ? categoryFilter : undefined)}
              >
                创建任务
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Schedule grid */}
      {filteredSchedules.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredSchedules.map(({ schedule, category }) => (
            <ScheduleCard
              key={`${category}-${schedule.id}`}
              schedule={schedule}
              category={category}
              onToggle={(id, enabled) => handleToggle(id, enabled, category)}
              onRunNow={(id) => handleRunNow(id, category)}
              onEdit={onEdit ? () => onEdit(schedule, category) : () => {}}
              onDelete={() => setDeleteTarget({ schedule, category })}
              onViewHistory={onViewHistory ? () => onViewHistory(schedule.id, schedule.name, category) : undefined}
              isLoading={isActionLoading}
            />
          ))}
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setDeleteTarget(null)}
          />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <h3 className="text-lg font-semibold mb-2">确认删除</h3>
            <p className="text-gray-600 mb-4">
              确定要删除定时任务 "{deleteTarget.schedule.name}" 吗？此操作无法撤销。
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
                取消
              </Button>
              <Button
                variant="primary"
                onClick={handleDeleteConfirm}
                className="bg-red-600 hover:bg-red-700"
              >
                删除
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
